import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Lock, Check, X, QrCode } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";

export default function MFASetup() {
  const { user, refreshUser } = useAuth();
  const [setupOpen, setSetupOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const mfaEnabled = !!user?.mfa_enabled;

  const startSetup = async () => {
    setLoading(true);
    try {
      const res = await api.post("/auth/mfa/setup");
      setSetupData(res.data);
      setSetupOpen(true);
      setCode("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao configurar MFA");
    }
    setLoading(false);
  };

  const verifyAndEnable = async () => {
    if (code.length !== 6) return;
    setLoading(true);
    try {
      await api.post("/auth/mfa/verify", { code });
      await refreshUser();
      toast.success("Autenticação de dois fatores ativada!");
      setSetupOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Código inválido");
    }
    setLoading(false);
  };

  const disableMFA = async () => {
    if (code.length !== 6) return;
    setLoading(true);
    try {
      await api.post("/auth/mfa/disable", { code });
      await refreshUser();
      toast.success("MFA desativado");
      setDisableOpen(false);
      setCode("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Código inválido");
    }
    setLoading(false);
  };

  if (user?.role === "employee") return null;

  return (
    <>
      <Card className="border-white/5" data-testid="mfa-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Lock className="w-4 h-4 text-blue-400" /> Autenticação de Dois Fatores (TOTP)
          </CardTitle>
          <CardDescription className="text-xs">
            {mfaEnabled ? "Ativado - sua conta está protegida" : "Proteja sua conta com um app autenticador"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-2.5 h-2.5 rounded-full ${mfaEnabled ? "bg-emerald-500" : "bg-yellow-500"}`} />
              <span className="text-sm font-medium">{mfaEnabled ? "Ativo" : "Inativo"}</span>
            </div>
            {mfaEnabled ? (
              <Button variant="outline" size="sm" data-testid="disable-mfa-btn"
                onClick={() => { setCode(""); setDisableOpen(true); }}
                className="border-red-500/20 text-red-400 hover:bg-red-500/10 text-xs">
                <X className="w-3 h-3 mr-1" /> Desativar
              </Button>
            ) : (
              <Button size="sm" data-testid="enable-mfa-btn" onClick={startSetup} disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs">
                <Shield className="w-3 h-3 mr-1" /> Ativar MFA
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Setup Dialog */}
      <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
        <DialogContent className="sm:max-w-md border-white/10" data-testid="mfa-setup-dialog">
          <DialogHeader>
            <DialogTitle>Configurar Autenticador</DialogTitle>
            <DialogDescription>Escaneie o QR code com seu app autenticador (Google Authenticator, Authy, etc.)</DialogDescription>
          </DialogHeader>
          {setupData && (
            <div className="space-y-5 py-4">
              <div className="flex justify-center">
                <div className="bg-white p-4 rounded-xl">
                  <QRCodeSVG value={setupData.uri} size={180} data-testid="mfa-qrcode" />
                </div>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-1">Ou insira o código manualmente:</p>
                <code className="text-xs bg-white/5 px-3 py-1.5 rounded font-mono tracking-wider" data-testid="mfa-secret">
                  {setupData.secret}
                </code>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Digite o código de 6 dígitos do app:</Label>
                <Input data-testid="mfa-code-input" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000" className="h-11 text-center text-lg tracking-[0.5em] font-mono bg-black/20 border-white/10"
                  maxLength={6} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSetupOpen(false)} className="border-white/10">Cancelar</Button>
            <Button data-testid="verify-mfa-btn" onClick={verifyAndEnable} disabled={loading || code.length !== 6}
              className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {loading ? <div className="loading-spinner" /> : <><Check className="w-4 h-4 mr-1" /> Verificar e Ativar</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable Dialog */}
      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent className="sm:max-w-sm border-white/10" data-testid="mfa-disable-dialog">
          <DialogHeader>
            <DialogTitle>Desativar MFA</DialogTitle>
            <DialogDescription>Insira o código do autenticador para confirmar</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input data-testid="mfa-disable-code-input" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000" className="h-11 text-center text-lg tracking-[0.5em] font-mono bg-black/20 border-white/10"
              maxLength={6} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisableOpen(false)} className="border-white/10">Cancelar</Button>
            <Button data-testid="confirm-disable-mfa-btn" onClick={disableMFA} disabled={loading || code.length !== 6}
              className="bg-red-600 hover:bg-red-700 text-white">
              {loading ? <div className="loading-spinner" /> : "Desativar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
